/*
 * Purpose: Stripe Pricing Table — US + UK embeds from Vite env (Render static site build)
 * Last modified: 2026-07-22 | Agent
 * Completeness: 100
 */

export type StripePricingRegion = 'us' | 'uk';

type StripePricingEmbed = {
  pricingTableId: string;
  publishableKey: string;
};

const PRICING_TABLE_ID_RE = /pricing-table-id=["'](prctbl_[^"']+)["']/i;
const PUBLISHABLE_KEY_RE = /publishable-key=["'](pk_(?:live|test)_[^"']+)["']/i;
const PRICING_TABLE_ID_ONLY_RE = /^prctbl_[A-Za-z0-9]+$/;

/**
 * Public Stripe Pricing Table defaults — safe in client bundle (pk_live + prctbl are public).
 * Used when Vite env was not injected at build time (Render/Pages env group gap).
 */
const PRODUCTION_STRIPE_DEFAULTS = {
  pricingTableId: 'prctbl_1TnM5WBsz6Xe3f1UuPN5RHuZ',
  publishableKey:
    'pk_live_51Tf8QHBsz6Xe3f1U2aiKXsrs9auPbKK3lY1embb8nPqsQue8wlY7XJFbuLYfDhrXxLgB8yW1jsxFWTBwRzIcHl0200z2BMTWJs',
} as const;

function productionStripeFallback(): StripePricingEmbed {
  if (!import.meta.env.PROD) return { pricingTableId: '', publishableKey: '' };
  return { ...PRODUCTION_STRIPE_DEFAULTS };
}

/** Parse Stripe embed HTML or attribute snippet from Render env. */
export function parseStripePricingEmbed(raw: string | undefined): StripePricingEmbed {
  const value = raw?.trim() ?? '';
  if (!value) return { pricingTableId: '', publishableKey: '' };

  if (PRICING_TABLE_ID_ONLY_RE.test(value)) {
    return { pricingTableId: value, publishableKey: '' };
  }

  const pricingTableId = value.match(PRICING_TABLE_ID_RE)?.[1]?.trim() ?? '';
  const publishableKey = value.match(PUBLISHABLE_KEY_RE)?.[1]?.trim() ?? '';
  return { pricingTableId, publishableKey };
}

/** US pricing table — Render key: VITE_STRIPE_PRICING_TABLE (full embed or prctbl_ id). */
function readUsEmbedEnv(): StripePricingEmbed {
  const fromTable =
    (import.meta.env.VITE_STRIPE_PRICING_TABLE as string | undefined) ??
    (import.meta.env.VITE_STRIPE_PRICING_TABLE_ID as string | undefined);
  return parseStripePricingEmbed(fromTable);
}

/** UK pricing table — Render key: VITE_STRIPE_PRICING_TABLE_UK (full embed or prctbl_ id). */
function readUkEmbedEnv(): StripePricingEmbed {
  return parseStripePricingEmbed(import.meta.env.VITE_STRIPE_PRICING_TABLE_UK as string | undefined);
}

/** True when browser locale/timezone suggests UK (en-GB or Europe/London). */
export function isUkPricingRegion(): boolean {
  if (typeof navigator === 'undefined') return false;
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  if (langs.some((l) => l?.toLowerCase().startsWith('en-gb'))) return true;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone === 'Europe/London';
  } catch {
    return false;
  }
}

/** Which regional pricing table to show: UK embed when UK visitor, otherwise US. */
export function getStripePricingRegion(): StripePricingRegion {
  const ukId = readUkEmbedEnv().pricingTableId;
  if (isUkPricingRegion() && ukId) return 'uk';
  return 'us';
}

function readActiveEmbedEnv(): StripePricingEmbed {
  return getStripePricingRegion() === 'uk' ? readUkEmbedEnv() : readUsEmbedEnv();
}

/** Stripe publishable key (pk_live_… or pk_test_…) — safe in browser bundles. */
export function getStripePublishableKey(): string {
  const activeEmbed = readActiveEmbedEnv();
  if (activeEmbed.publishableKey) return activeEmbed.publishableKey;

  const explicit = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined)?.trim();
  if (explicit) return explicit;

  const legacy = (import.meta.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string | undefined)?.trim();
  if (legacy) return legacy;

  const fromUsEmbed = readUsEmbedEnv().publishableKey;
  if (fromUsEmbed) return fromUsEmbed;

  return productionStripeFallback().publishableKey;
}

function getUsStripePricingTableId(): string {
  const fromEmbed = readUsEmbedEnv().pricingTableId;
  if (fromEmbed) return fromEmbed;
  const fromId = (import.meta.env.VITE_STRIPE_PRICING_TABLE_ID as string | undefined)?.trim();
  if (fromId) return fromId;
  return productionStripeFallback().pricingTableId;
}

/** Pricing table id for the visitor's region (UK or US). */
export function getStripePricingTableId(): string {
  if (getStripePricingRegion() === 'uk') return readUkEmbedEnv().pricingTableId;
  return getUsStripePricingTableId();
}

export function isStripeUsPricingConfigured(): boolean {
  return Boolean(getUsStripePricingTableId());
}

export function isStripeUkPricingConfigured(): boolean {
  return Boolean(readUkEmbedEnv().pricingTableId);
}

export function isStripePricingConfigured(): boolean {
  if (getStripePricingRegion() === 'uk') {
    return Boolean(getStripePublishableKey() && readUkEmbedEnv().pricingTableId);
  }
  return Boolean(getStripePublishableKey() && getUsStripePricingTableId());
}

/**
 * Render env (SPA rebuild required):
 * - VITE_STRIPE_PRICING_TABLE — US embed
 * - VITE_STRIPE_PRICING_TABLE_UK — UK embed
 * - VITE_STRIPE_PUBLISHABLE_KEY — optional if each embed includes publishable-key
 */
export const STRIPE_PRICING_RENDER_ENV_KEYS = [
  'VITE_STRIPE_PRICING_TABLE',
  'VITE_STRIPE_PRICING_TABLE_UK',
  'VITE_STRIPE_PUBLISHABLE_KEY',
  'VITE_STRIPE_ALLOW_PROMOTION_CODES',
] as const;

/**
 * Promotion codes: Checkout Sessions from POST /api/billing/checkout always set
 * allow_promotion_codes. This flag only controls the pricing-page promo hint copy.
 * Pricing Table embed fallback still needs Dashboard → Allow promotion codes.
 */
export function isStripePromotionCodesEnabled(): boolean {
  const raw = (import.meta.env.VITE_STRIPE_ALLOW_PROMOTION_CODES as string | undefined)?.trim();
  if (raw === '0' || raw?.toLowerCase() === 'false') return false;
  return true;
}
